import { SERVICE_NAME_MAP } from '@Utils/service';
import { getUtcTimestamp } from '@Utils/time';
import {
  CallbackDataParams,
  TooltipFormatterCallback,
} from 'echarts/types/dist/shared';
import { BarSeriesOption, LineSeriesOption } from 'echarts';

import { t } from 'i18next';
import merge from 'lodash/merge';
import {
  ADMIN_CONFIRMED_USER_COMPROMISED,
  atRiskUserCategoryTranslation,
  SUSPICIOUS_CREDENTIAL_ACCESS,
  SUSPICIOUS_SIGN_IN,
  SUSPICIOUS_USER_BEHAVIOR,
} from '@Dashboard/RiskUser/UserRiskAnalytics/utils';

export const fontFamily =
  '"Segoe UI",-apple-system,BlinkMacSystemFont,"Helvetica Neue",Helvetica,Arial,sans-serif';

export const timeItemTooltipFormatter: TooltipFormatterCallback<
  CallbackDataParams
> = (params) => {
  return `${params.name}<br />${params.marker}${params.seriesName?.replace(/<|>/g, ' ').replace(/>/g, '\\>')}<span style="float: right; margin-left: 20px"><b>${params.data}</b></span>`;
};

export const getServiceColor = (service: string) => {
  return {
    [t('common.service.exchange')]: '#8F41E9',
    [t('common.service.sharepoint')]: '#10B4D3',
    [t('common.service.onedrive')]: '#FF7633',
    [t('common.service.box')]: '#FABA2A',
    [t('common.service.dropbox')]: '#95B7FC',
    [t('common.service.googledrive')]: '#578AEF',
    [t('common.service.salesforce_sandbox')]: '#F46F71',
    [t('common.service.salesforce_production')]: '#04CAA1',
    [t('common.service.gmail')]: '#E94181',
    [t('common.service.gmail_inline')]: '#CA0455',
    [t('common.service.teams')]: '#F24C4F',
    [t('common.service.teams_chat')]: '#BB89F6',
    [t('common.service.exchangeserver')]: '#1E5EDE',
    [t('common.service.exchange_inline')]: '#04C45A',
    [t('dashboard.chart.column.url')]: '#04CAA1',
    [t('dashboard.chart.column.file')]: '#578AEF',
  }[service];
};

export const dashboardServiceNameTranslate = (service: string) => {
  const namesMap: Record<string, string> = {
    URL: t('dashboard.chart.column.url'),
    FILE: t('dashboard.chart.column.file'),
  };
  SERVICE_NAME_MAP.forEach((value: string, key: string) => {
    namesMap[key] = t(`common.service.${value}`);
  });
  return namesMap[service];
};

export const dashboardTimerangeYData = (timerange: Record<string, number>) => {
  return Object.entries(timerange ?? {})
    .sort()
    .map(([, value]) => value);
};

export const dashboardTimerangeXData = (
  timerange: Record<string, any>,
  utcOffset?: string
) => {
  return Object.entries(timerange ?? {})
    .sort()
    .map(([date]) => getUtcTimestamp(date, utcOffset));
};

export const baseLineSeriesOption: LineSeriesOption = {
  type: 'line',
  symbolSize: 6,
  symbol: 'circle',
  sampling: 'none',
  itemStyle: {
    borderWidth: 15,
    borderColor: 'transparent',
  },
  emphasis: {
    focus: 'series',
    scale: 1.25,
  },
  tooltip: {
    position: 'top',
  },
};

export const dashboardServiceLineSeries = (params: {
  data: Record<string, { timerange: Record<string, number> }>;
  service: string;
}): LineSeriesOption => {
  const { data, service } = params;
  return merge<LineSeriesOption, LineSeriesOption>(
    {
      data: dashboardTimerangeYData(data[service].timerange),
      name: dashboardServiceNameTranslate(service),
      color: getServiceColor(dashboardServiceNameTranslate(service)),
      tooltip: {
        formatter: timeItemTooltipFormatter,
      },
    },
    baseLineSeriesOption
  );
};

export const dashboarRiskUserLineSeries = (params: {
  data: Record<string, Record<string, number>>;
  user: string;
  index: number;
}): LineSeriesOption => {
  const { data, user, index } = params;
  const topRiskUserColors = [
    '#578AEF',
    '#04C45A',
    '#FF7633',
    '#AB6FF3',
    '#10B4D3',
  ];
  return merge<LineSeriesOption, LineSeriesOption>(
    {
      data: dashboardTimerangeYData(data[user]),
      name: user,
      color: topRiskUserColors[index],
      tooltip: {
        formatter: timeItemTooltipFormatter,
      },
    },
    baseLineSeriesOption
  );
};

export const baseBarSeriesOption: BarSeriesOption = {
  type: 'bar',
  emphasis: {
    focus: 'series',
  },
  barCategoryGap: '30%',
  tooltip: {
    position: 'top',
  },
};

export const dashboardHighRiskTrendsBarSeries = (params: {
  data: Record<string, Record<string, number>>;
  category: string;
}): BarSeriesOption => {
  const { data, category } = params;
  const colors: Record<string, string> = {
    Phishing: '#578AEF',
    MaliciousURL: '#04CAA1',
    MaliciousFile: '#AB6FF3',
    Ransomware: '#FBA96E',
    BEC: '#10B4D3',
    Graymail: '#FABA2A',
    OtherSpam: '#FF7633',
  };
  const riskNameTranslation: Record<string, string> = {
    Phishing: t('riskUser.topUser.phishing'),
    MaliciousURL: t('riskUser.topUser.maliciousUrl'),
    MaliciousFile: t('riskUser.topUser.maliciousFile'),
    Ransomware: t('riskUser.topUser.ransomware'),
    Graymail: t('riskUser.topUser.graymail'),
    OtherSpam: t('riskUser.topUser.otherSpam'),
  };
  return merge<BarSeriesOption, BarSeriesOption>(
    {
      data: dashboardTimerangeYData(data[category]),
      name: riskNameTranslation[category],
      color: colors[category],
      stack: 'total',
      tooltip: {
        formatter: timeItemTooltipFormatter,
      },
    },
    baseBarSeriesOption
  );
};

export const dashboardAtRiskUserTrendsLineSeries = (params: {
  data: Record<string, Record<string, { total: number }>>;
  category: string;
}): LineSeriesOption => {
  const { data, category } = params;
  const colors: Record<string, string> = {
    [SUSPICIOUS_SIGN_IN]: '#578AEF',
    [SUSPICIOUS_CREDENTIAL_ACCESS]: '#04CAA1',
    [SUSPICIOUS_USER_BEHAVIOR]: '#AB6FF3',
    [ADMIN_CONFIRMED_USER_COMPROMISED]: '#FBA96E',
  };
  return merge<LineSeriesOption, LineSeriesOption>(
    {
      data: Object.entries(data[category])
        .sort()
        .map(([, { total }]) => total),
      name: atRiskUserCategoryTranslation(category),
      color: colors[category],
      areaStyle: {
        color: colors[category],
        opacity: 0.15,
      },
      tooltip: {
        formatter: timeItemTooltipFormatter,
      },
    },
    baseLineSeriesOption
  );
};

export const dashboardAtRiskUserTrendsBarSeries = (params: {
  data: Record<string, Record<string, number>>;
  risk: string;
}): BarSeriesOption => {
  const { data, risk } = params;
  const colors: Record<string, string> = {
    Phishing: '#578AEF',
    MaliciousURL: '#04CAA1',
    MaliciousFile: '#AB6FF3',
    Ransomware: '#FBA96E',
    BEC: '#10B4D3',
    Graymail: '#FABA2A',
    OtherSpam: '#FF7633',
  };
  const riskNameTranslation: Record<string, string> = {
    Phishing: t('riskUser.topUser.phishing'),
    MaliciousURL: t('riskUser.topUser.maliciousUrl'),
    MaliciousFile: t('riskUser.topUser.maliciousFile'),
    Ransomware: t('riskUser.topUser.ransomware'),
    Graymail: t('riskUser.topUser.graymail'),
    OtherSpam: t('riskUser.topUser.otherSpam'),
  };
  return merge<BarSeriesOption, BarSeriesOption>(
    {
      data: dashboardTimerangeYData(data[risk]),
      name: riskNameTranslation[risk],
      color: colors[risk],
      stack: 'total',
      tooltip: {
        formatter: timeItemTooltipFormatter,
      },
    },
    baseBarSeriesOption
  );
};
